/**
 * Popover Component
 *
 * A versatile popover component for displaying content in a floating panel
 * triggered by clicking or hovering over an element.
 */
import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { cn } from '../../utils/tailwind-utils'

interface PopoverProps {
  /**
   * The element that triggers the popover
   */
  children: React.ReactNode

  /**
   * The content to display in the popover
   */
  content: React.ReactNode

  /**
   * The position of the popover relative to the trigger
   * @default "bottom"
   */
  position?: 'top' | 'right' | 'bottom' | 'left'

  /**
   * The alignment of the popover
   * @default "center"
   */
  align?: 'start' | 'center' | 'end'

  /**
   * The trigger behavior
   * @default "click"
   */
  trigger?: 'click' | 'hover'

  /**
   * Additional CSS classes for the popover
   */
  className?: string

  /**
   * Whether the popover is open (controlled mode)
   */
  open?: boolean

  /**
   * Function called when the popover's open state changes (controlled mode)
   */
  onOpenChange?: (open: boolean) => void

  /**
   * Whether the popover should close when clicking outside
   * @default true
   */
  closeOnOutsideClick?: boolean

  /**
   * Whether the popover should close when pressing Escape
   * @default true
   */
  closeOnEsc?: boolean

  /**
   * The width of the popover
   * @default "auto"
   */
  width?: number | string

  /**
   * The offset from the trigger element in pixels
   * @default 8
   */
  offset?: number
}

/**
 * Popover component for displaying content in a floating panel
 *
 * @example
 * // Basic usage with click trigger
 * <Popover content={<div className="p-4">Popover content</div>}>
 *   <Button>Click me</Button>
 * </Popover>
 *
 * @example
 * // With hover trigger and position
 * <Popover
 *   content={<div className="p-2">Hover tooltip</div>}
 *   trigger="hover"
 *   position="top"
 * >
 *   <InfoIcon />
 * </Popover>
 *
 * @example
 * // Controlled mode
 * <Popover
 *   content={<div>Controlled popover</div>}
 *   open={isOpen}
 *   onOpenChange={setIsOpen}
 * >
 *   <Button>Toggle</Button>
 * </Popover>
 */
function Popover({
  children,
  content,
  position = 'bottom',
  align = 'center',
  trigger = 'click',
  className,
  open: controlledOpen,
  onOpenChange,
  closeOnOutsideClick = true,
  closeOnEsc = true,
  width = 'auto',
  offset = 8,
}: PopoverProps) {
  const [internalOpen, setInternalOpen] = useState(false)
  const [coords, setCoords] = useState({ x: 0, y: 0 })
  const triggerRef = useRef<HTMLDivElement>(null)
  const popoverRef = useRef<HTMLDivElement>(null)
  const timeoutRef = useRef<NodeJS.Timeout | null>(null)
  const [isMounted, setIsMounted] = useState(false)

  // Determine if component is controlled or uncontrolled
  const isControlled = controlledOpen !== undefined
  const isOpen = isControlled ? controlledOpen : internalOpen

  // Handle mounting state for SSR compatibility
  useEffect(() => {
    setIsMounted(true)
    return () => setIsMounted(false)
  }, [])

  // Update internal state when controlled state changes
  useEffect(() => {
    if (isControlled && controlledOpen !== internalOpen) {
      setInternalOpen(controlledOpen)
    }
  }, [controlledOpen, isControlled, internalOpen])

  // Calculate popover position
  const calculatePosition = () => {
    if (!triggerRef.current || !popoverRef.current) return

    const triggerRect = triggerRef.current.getBoundingClientRect()
    const popoverRect = popoverRef.current.getBoundingClientRect()

    let x = 0
    let y = 0

    // Position based on specified position and alignment
    switch (position) {
      case 'top':
        y = triggerRect.top - popoverRect.height - offset

        if (align === 'start') {
          x = triggerRect.left
        } else if (align === 'end') {
          x = triggerRect.right - popoverRect.width
        } else {
          x = triggerRect.left + triggerRect.width / 2 - popoverRect.width / 2
        }
        break

      case 'right':
        x = triggerRect.right + offset

        if (align === 'start') {
          y = triggerRect.top
        } else if (align === 'end') {
          y = triggerRect.bottom - popoverRect.height
        } else {
          y = triggerRect.top + triggerRect.height / 2 - popoverRect.height / 2
        }
        break

      case 'bottom':
        y = triggerRect.bottom + offset

        if (align === 'start') {
          x = triggerRect.left
        } else if (align === 'end') {
          x = triggerRect.right - popoverRect.width
        } else {
          x = triggerRect.left + triggerRect.width / 2 - popoverRect.width / 2
        }
        break

      case 'left':
        x = triggerRect.left - popoverRect.width - offset

        if (align === 'start') {
          y = triggerRect.top
        } else if (align === 'end') {
          y = triggerRect.bottom - popoverRect.height
        } else {
          y = triggerRect.top + triggerRect.height / 2 - popoverRect.height / 2
        }
        break
    }

    // Ensure popover stays within viewport
    const padding = 10
    const viewportWidth = window.innerWidth
    const viewportHeight = window.innerHeight

    // Adjust horizontal position
    if (x < padding) {
      x = padding
    } else if (x + popoverRect.width > viewportWidth - padding) {
      x = viewportWidth - popoverRect.width - padding
    }

    // Adjust vertical position
    if (y < padding) {
      y = padding
    } else if (y + popoverRect.height > viewportHeight - padding) {
      y = viewportHeight - popoverRect.height - padding
    }

    setCoords({ x, y })
  }

  // Toggle popover open state
  const togglePopover = () => {
    const newOpenState = !isOpen

    if (!isControlled) {
      setInternalOpen(newOpenState)
    }

    if (onOpenChange) {
      onOpenChange(newOpenState)
    }

    if (newOpenState) {
      // Wait for next frame to calculate position after popover is rendered
      requestAnimationFrame(() => calculatePosition())
    }
  }

  // Handle mouse enter for hover trigger
  const handleMouseEnter = () => {
    if (trigger === 'hover') {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
        timeoutRef.current = null
      }

      if (!isOpen) {
        if (!isControlled) {
          setInternalOpen(true)
        }

        if (onOpenChange) {
          onOpenChange(true)
        }

        // Wait for next frame to calculate position after popover is rendered
        requestAnimationFrame(() => calculatePosition())
      }
    }
  }

  // Handle mouse leave for hover trigger
  const handleMouseLeave = () => {
    if (trigger === 'hover') {
      timeoutRef.current = setTimeout(() => {
        if (!isControlled) {
          setInternalOpen(false)
        }

        if (onOpenChange) {
          onOpenChange(false)
        }
      }, 100)
    }
  }

  // Handle clicking outside to close popover
  useEffect(() => {
    if (!isOpen || !closeOnOutsideClick) return

    function handleClickOutside(event: MouseEvent) {
      if (
        triggerRef.current &&
        popoverRef.current &&
        !triggerRef.current.contains(event.target as Node) &&
        !popoverRef.current.contains(event.target as Node)
      ) {
        if (!isControlled) {
          setInternalOpen(false)
        }

        if (onOpenChange) {
          onOpenChange(false)
        }
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [isOpen, closeOnOutsideClick, isControlled, onOpenChange])

  // Handle ESC key to close popover
  useEffect(() => {
    if (!isOpen || !closeOnEsc) return

    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        if (!isControlled) {
          setInternalOpen(false)
        }

        if (onOpenChange) {
          onOpenChange(false)
        }
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [isOpen, closeOnEsc, isControlled, onOpenChange])

  // Update position on window resize
  useEffect(() => {
    calculatePosition()
    window.addEventListener('resize', calculatePosition)
    window.addEventListener('scroll', calculatePosition)

    return () => {
      window.removeEventListener('resize', calculatePosition)
      window.removeEventListener('scroll', calculatePosition)
    }
  }, [isOpen, calculatePosition])

  // Clean up timeout on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
    }
  }, [])

  // Position classes for arrow
  const arrowPositionClasses = {
    top: 'bottom-[-6px] left-1/2 transform -translate-x-1/2 rotate-45',
    right: 'left-[-6px] top-1/2 transform -translate-y-1/2 rotate-45',
    bottom: 'top-[-6px] left-1/2 transform -translate-x-1/2 rotate-45',
    left: 'right-[-6px] top-1/2 transform -translate-y-1/2 rotate-45',
  }

  return (
    <div
      ref={triggerRef}
      className="inline-flex"
      onClick={trigger === 'click' ? togglePopover : undefined}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {children}

      {isMounted &&
        isOpen &&
        createPortal(
          <div
            ref={popoverRef}
            className={cn(
              'fixed z-50 bg-card text-card-foreground border border-border rounded-md shadow-md animate-in fade-in-0 zoom-in-95 duration-100',
              className
            )}
            style={{
              left: `${coords.x}px`,
              top: `${coords.y}px`,
              width: typeof width === 'number' ? `${width}px` : width,
            }}
            onMouseEnter={trigger === 'hover' ? handleMouseEnter : undefined}
            onMouseLeave={trigger === 'hover' ? handleMouseLeave : undefined}
            role="dialog"
          >
            {content}
            <div
              className={cn(
                'absolute w-3 h-3 bg-card border-border',
                arrowPositionClasses[position]
              )}
              style={{
                borderWidth:
                  position === 'bottom' || position === 'right' ? '1px 0 0 1px' : '0 1px 1px 0',
              }}
            />
          </div>,
          document.body
        )}
    </div>
  )
}

export default Popover
